import React, { type FC, useContext } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import IntegrationsBanner, { type IntegrationsBannerProps } from '../banner/integrations/integrations-banner';
import Text from '../text/text';
import Link from '../link/link';
import LayoutContext from '../context/layout-context';

export interface IntegrationsBannerSectionProps extends IntegrationsBannerProps {
  /** The heading (h2) of the Integration Banner Section */
  heading?: string;
  /** The accompanying text of the Integration Banner Section */
  text?: string;
  /** The section CTA */
  link?: Lucid.Directus.Generated.Links;
}

const IntegrationsBannerSection: FC<IntegrationsBannerSectionProps> = ({
  className,
  heading,
  text,
  link,
  integrations,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_nav_integrations'],
    shortCode,
  });

  return (
    <Box
      as="section"
      css={theme => ({ paddingTop: theme.space.s120, paddingBottom: theme.space.s80 })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container extraPadding={3}>
        <Text css={theme => ({ color: theme.colors.coolGrey[60] })} variant="interactiveM500">
          {withSmartling('mktg_nav_integrations')}
        </Text>
        {heading && (
          <Heading
            as="h2"
            css={theme => ({ maxWidth: '640px', [theme.breakpoints.bp900.min]: { maxWidth: '740px' } })}
            variant="heading2XL"
          >
            {heading}
          </Heading>
        )}
        {text && (
          <Text css={{ maxWidth: '640px' }} variant="paragraphM400">
            {text}
          </Text>
        )}
        {link?.final_url && (
          <Link href={link?.final_url} iconAnimation="navigational">
            {link?.default_link_text}
          </Link>
        )}
      </Container>
      <IntegrationsBanner integrations={integrations} />
    </Box>
  );
};

export { IntegrationsBannerSection };
export default IntegrationsBannerSection;
